import React, {
  useState,
  useRef,
  useEffect,
  useId,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

import { Tooltip } from "./Tooltip";

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: "default" | "danger";
  divider?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  /**
   * Why this item is unavailable, shown as the app's own tooltip rather than
   * the browser's. Its main use is explaining a `disabled` item — an action
   * greyed out with no reason given is a dead end.
   */
  title?: string;
  className?: string;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  ariaLabel?: string;
  buttonClassName?: string;
  menuClassName?: string;
  /** Preferred horizontal alignment of the menu relative to the button. */
  align?: "left" | "right";
}

const MENU_WIDTH = 176; // matches w-44 (44 * 4px)
const MENU_GAP = 4; // px between button and menu
const VIEWPORT_MARGIN = 8; // px breathing room from viewport edges

const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  ariaLabel = "Actions",
  buttonClassName,
  menuClassName,
  align = "right",
}) => {
  const [open, setOpen] = useState(false);
  // Unique per menu, so the `aria-describedby` wiring between a row and its
  // tooltip cannot collide across the dozens of these a table renders.
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    placement: "down" | "up";
  } | null>(null);

  const visibleItems = items.filter((i) => !i.hidden);

  // Compute portal-anchored position, with 4-way clamping against the viewport.
  const computePosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Estimate menu height from item count — close enough for placement decisions.
    // Each item ≈ 36px, plus 8px vertical padding; dividers add ~9px.
    const itemCount = visibleItems.length;
    const dividerCount = visibleItems.filter((i) => i.divider).length;
    const estimatedHeight = 8 + itemCount * 36 + dividerCount * 9;

    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;
    const placement: "down" | "up" =
      spaceBelow >= estimatedHeight + MENU_GAP + VIEWPORT_MARGIN ||
      spaceBelow >= spaceAbove
        ? "down"
        : "up";

    const top =
      placement === "down" ? rect.bottom + MENU_GAP : rect.top - MENU_GAP;

    // Horizontal: start from preferred alignment, then clamp into viewport.
    let left =
      align === "right" ? rect.right - MENU_WIDTH : rect.left;

    // Clamp horizontally so the menu never goes off-screen.
    if (left + MENU_WIDTH > vw - VIEWPORT_MARGIN) {
      left = vw - MENU_WIDTH - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;

    // For "up" placement we anchor to the bottom edge of the menu.
    // We use a CSS translate for that, but we still pass `top` as the anchor.
    setCoords({ top, left, placement });
  };

  // Compute on open + when scrolling/resizing while open.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    computePosition();
    const handler = () => computePosition();
    window.addEventListener("scroll", handler, true); // capture: catch nested scrolls
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click (portal-aware — check both refs).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleItemClick =
    (item: ActionMenuItem) => (e: React.MouseEvent) => {
      e.stopPropagation();
      if (item.disabled) return;
      item.onClick();
      setOpen(false);
    };

  if (visibleItems.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={
          buttonClassName ??
          "p-1.5 rounded-md text-[#8A9590] hover:text-[#0B0F0E] hover:bg-[#F5F4F0] transition-colors"
        }
        title={ariaLabel}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        type="button"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                width: MENU_WIDTH,
                transform:
                  coords.placement === "up" ? "translateY(-100%)" : undefined,
                zIndex: 9999,
              }}
              className={`bg-white rounded-lg shadow-lg border border-[rgba(11,15,14,0.10)] py-1 ${
                menuClassName ?? ""
              }`}
              // Stop click propagation so clicks inside don't bubble to anything outside.
              onClick={(e) => e.stopPropagation()}
            >
              {visibleItems.map((item, i) => {
                const variantClass = item.className
                  ? item.className
                  : item.variant === "danger"
                  ? "text-[#E24B4A] hover:bg-[#fdf5ec]"
                  : "text-[#0B0F0E] hover:bg-[#F5F4F0]";
                // `pointer-events-none` is deliberately not used: it would also
                // suppress the tooltip that explains why the item is disabled.
                const disabledClass = item.disabled
                  ? "opacity-50 cursor-not-allowed"
                  : "";
                return (
                  <React.Fragment key={i}>
                    {item.divider && i > 0 && (
                      <div className="my-1 border-t border-[rgba(11,15,14,0.06)]" />
                    )}
                    {/*
                     * The reason an item is unavailable, in the app's own
                     * furniture.
                     *
                     * It used to be the browser's native `title`, which is the
                     * wrong instrument for a sentence somebody has to act on: it
                     * waits a second, renders in the OS's chrome in the OS's
                     * font, cannot be reached from a keyboard, and does not
                     * exist at all on a touchscreen. These reasons are written
                     * by the server and name the thing to go and fix — an
                     * invoice to void, a container that has started — so they
                     * have to be readable by everyone the menu is.
                     *
                     * `Tooltip` renders nothing extra when there is no text, so
                     * ordinary items are untouched. It is a `block w-full` span
                     * because it sits between the menu's column layout and a
                     * full-width row; inline would collapse the row to its
                     * label.
                     *
                     * Note `pointer-events-none` is still deliberately absent
                     * from `disabledClass` above: it would suppress the hover
                     * this depends on.
                     */}
                    <Tooltip
                      text={item.title}
                      id={item.title ? `${menuId}-tip-${i}` : undefined}
                      className="block w-full"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleItemClick(item)}
                        aria-disabled={item.disabled || undefined}
                        aria-describedby={
                          item.title ? `${menuId}-tip-${i}` : undefined
                        }
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm font-medium transition-colors ${variantClass} ${disabledClass}`}
                      >
                        <span className="shrink-0">{item.icon}</span>
                        <span className="min-w-0 flex-1 truncate">
                          {item.label}
                        </span>
                      </button>
                    </Tooltip>
                  </React.Fragment>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

export default ActionMenu;
